// UserInfoQuestionSetEntriesTab.js
import React, { useState, useRef, useEffect } from "react";
import { 
  Row, 
  Col, 
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
} from "reactstrap";
import { withTranslation } from "react-i18next";
import axiosInstance from "utils/axiosInstance";

// Select and DND
import Select, { components } from 'react-select';
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import { uploadImage, fetchImage } from "utils/images/imageManager";

// Placeholders
import placeholderSmall from "assets/images/info/card-placeholder-small.png";

// Components
import StatusSelect from "../components/StatusSelect";
import { CircleCheckBig, Goal } from 'lucide-react';
import { CornerRightDown, Plus } from "react-feather";


const QuestionSetEntriesTab = ({
  t,
  infoQuestionSetId,
  infoQuestionSetEntries,
  setInfoQuestionSetEntries,
  isSavingStatus,
  setIsSavingStatus,
  isSavedStatus,
  setIsSavedStatus,
  imageMap,
  setImageMap,
  navigate,
  setError,
}) => {

  useEffect(()=>{
    console.log('imageMap:', imageMap);
  },[imageMap])
  

  //--------------------------------------------------
  // States
  //--------------------------------------------------
  const [openSelectId, setOpenSelectId] = useState(null);

  //--------------------------------------------------
  // DND
  //--------------------------------------------------

  const [isSavingEntriesOrder, setIsSavingEntriesOrder] = useState(false);
  const [isSavedEntriesOrder, setIsSavedEntriesOrder] = useState(true);
  const [isSavedEntriesOrderError, setIsSavedEntriesOrderError] = useState(false);

  // Fires when dropping dragged items
  const handleDragEnd = (result) => {
    setIsSavedEntriesOrderError(false);
    setIsSavedEntriesOrder(false);
    // If there's no valid destination (e.g. dropped outside), do nothing
    if (!result.destination) return;

    // Reorder the array in state
    const newOrder = Array.from(infoQuestionSetEntries);
    const [movedItem] = newOrder.splice(result.source.index, 1);
    newOrder.splice(result.destination.index, 0, movedItem);

    setInfoQuestionSetEntries(newOrder);

    handleSaveEntriesOrder(newOrder);
  };

  // Saving new order
  const handleSaveEntriesOrder = async (newOrder) => {
    try {

      const payload = newOrder.map((q, index) => ({
        _id: q._id,
        type: q.type,
        label: q.label,
        order: index + 1,
      }));

      console.log('payload:', payload);

      setIsSavingEntriesOrder(true);
      await axiosInstance.patch(
        `/knovia/myInfo/userInfoQuestionSet/${infoQuestionSetId}/reorderEntries`,
        payload
      );

      // Update local state after successful save
      const reorderedQuestions = payload.map(({ _id, order }) => {
        const matchingQuestion = infoQuestionSetEntries.find(q => q._id === _id);
        return { ...matchingQuestion, order };
      });

      setInfoQuestionSetEntries((prev) => {
        return reorderedQuestions.sort((a, b) => a.order - b.order);
      });

      //alert("Question order saved!");
      setIsSavingEntriesOrder(false);
      setIsSavedEntriesOrder(true);

    } catch (err) {
      console.error(err);
      setIsSavingEntriesOrder(false);
      setIsSavedEntriesOrderError(true);
      setError("Failed to save entries order.");
    }
  };

  //--------------------------------------------------
  // ADD/DELETE Questions
  //--------------------------------------------------


  // ADD a new InfoQuestion
  const handleAddQuestion = async () => {
    try {

      const newQuestionPayload = {
        questionSet: infoQuestionSetId,
        label: "New question"
      };

      const res = await axiosInstance.post("/knovia/myInfo/userInfoQuestion", newQuestionPayload);
      const newEntry = {
        ...res.data,
        type: "question"
      };

      setInfoQuestionSetEntries((prev) => {
        const updated = [...prev, newEntry];
        return updated.sort((a, b) => a.order - b.order);
      });

      setIsSavedStatus((prev) => ({
        ...prev,
        [res.data._id]: true
      }));

    } catch (err) {
      console.error(err);
      setError("Failed to add question.");
    }
  };


  // DELETE an existing InfoQuestion
  const handleDeleteQuestion = async (questionId) => {
    try {
      const deletedQuestion = infoQuestionSetEntries.find(
        (e) => e._id === questionId && e.type === "question"
      );
      if (!deletedQuestion) return;

      const deletedOrder = deletedQuestion.order;

      await axiosInstance.delete(`/knovia/myInfo/userInfoQuestion/${questionId}`);

      setInfoQuestionSetEntries((prev) => {
        return prev
          .filter((e) => e._id !== questionId)
          .map((e) =>
            e.order > deletedOrder ? { ...e, order: e.order - 1 } : e
          );
      });

      setIsSavedStatus((prev) => {
        const updated = { ...prev };
        delete updated[questionId];
        return updated;
      });
    } catch (err) {
      console.error(err);
      setError("Failed to delete question.");
    }
  };


  //--------------------------------------------------
  // ADD/DELETE Sections
  //--------------------------------------------------


  // ADD a new InfoQuestionSetSection
  const handleAddSection = async () => {
    try {

      const newSectionPayload = {
        questionSet: infoQuestionSetId,
        label: "New section"
      };

      const res = await axiosInstance.post("/knovia/myInfo/userInfoQuestionSetSection", newSectionPayload);
      const newEntry = {
        ...res.data,
        type: "section"
      };

      setInfoQuestionSetEntries((prev) => {
        const updated = [...prev, newEntry];
        return updated.sort((a, b) => a.order - b.order);
      });

      setIsSavedStatus((prev) => ({
        ...prev,
        [res.data._id]: true
      }));

    } catch (err) {
      console.error(err);
      setError("Failed to add section.");
    }
  };


  // DELETE an existing InfoQuestionSetSection
  const handleDeleteSection = async (sectionId) => {
    try {
      const deletedSection = infoQuestionSetEntries.find((q) => q._id === sectionId);
      if (!deletedSection) return;

      const deletedOrder = deletedSection.order;

      await axiosInstance.delete(`/knovia/myInfo/userInfoQuestionSetSection/${sectionId}`);

      setInfoQuestionSetEntries((prev) => {
        return prev
          .filter((e) => e._id !== sectionId)
          .map((e) =>
            e.order > deletedOrder ? { ...e, order: e.order - 1 } : e
          );
      });

      setIsSavedStatus((prev) => {
        const updated = { ...prev };
        delete updated[sectionId];
        return updated;
      });
    } catch (err) {
      console.error(err);
      setError("Failed to delete section.");
    }
  };

  //--------------------------------------------------
  // ADD/DELETE Contents
  //--------------------------------------------------


  // ADD a new InfoQuestionSetContent
  const handleAddContent = async (contentType) => {
    try {

      let label = contentType==="text" ? "New text" : "New image";
      let endpoint = contentType==="text" ? "userInfoQuestionSetTextContent" : "userInfoQuestionSetImageContent";

      const newContentPayload = {
        questionSet: infoQuestionSetId,
        label: label
      };

      const res = await axiosInstance.post(`/knovia/myInfo/${endpoint}`, newContentPayload);
      const newEntry = {
        ...res.data,
        type: contentType==="text" ? "text" : "image"
      };

      setInfoQuestionSetEntries((prev) => {
        const updated = [...prev, newEntry];
        return updated.sort((a, b) => a.order - b.order);
      });

      setIsSavedStatus((prev) => ({
        ...prev,
        [res.data._id]: true
      }));

    } catch (err) {
      console.error(err);
      setError("Failed to add content.");
    }
  };


  // DELETE an existing InfoQuestionSetContent
  const handleDeleteContent = async (contentId, type) => {
    try {
      const deletedContent = infoQuestionSetEntries.find((q) => q._id === contentId);
      if (!deletedContent) return;

      const deletedOrder = deletedContent.order;

      let contentType = type==="text" ? "userInfoQuestionSetTextContent" : "userInfoQuestionSetImageContent";

      await axiosInstance.delete(`/knovia/myInfo/${contentType}/${contentId}`);

      setInfoQuestionSetEntries((prev) => {
        return prev
          .filter((e) => e._id !== contentId)
          .map((e) =>
            e.order > deletedOrder ? { ...e, order: e.order - 1 } : e
          );
      });

      setIsSavedStatus((prev) => {
        const updated = { ...prev };
        delete updated[contentId];
        return updated;
      });
    } catch (err) {
      console.error(err);
      setError("Failed to delete content.");
    }
  };


  //--------------------------------------------------
  // Adjusting Statuses
  //--------------------------------------------------
  const statusOptions = [
    { value: 'needs_edit', label: '待編輯' },
    { value: 'in_progress', label: '編輯中' },
    { value: 'finalized', label: '已完成' },
  ];

  const handleStatusChange = async (entry, newStatus) => {
    // Set saving to true for this specific question
    setIsSavingStatus(prev => ({ ...prev, [entry._id]: true }));
    setIsSavedStatus(prev => ({ ...prev, [entry._id]: false }));

    try {
      const res = await axiosInstance.patch(`/knovia/myInfo/userInfoQuestion/${entry._id}/updateEntryStatus`, {
        status: newStatus,
        type: entry.type,
      });

      // Update local infoEntries with the new status
      setInfoQuestionSetEntries(prev =>
        prev.map(e => e._id === entry._id ? { ...e, status: newStatus } : e)
      );

      // Mark as saved
      setIsSavedStatus(prev => ({ ...prev, [entry._id]: true }));

    } catch (err) {
      console.error('Failed to update status:', err);
      setError("Failed to update entry status.");
    } finally {
      // Clear saving status
      setIsSavingStatus(prev => ({ ...prev, [entry._id]: false }));
    }
  };


  //--------------------------------------------------
  // Field Editing for Section/Text
  //-------------------------------------------------- 
  const [sectionEditModalOpen, setSectionEditModalOpen] = useState(false);
  const [sectionLabelValue, setSectionLabelValue] = useState("");
  const [editingSection, setEditingSection] = useState(null);

  const [selectedSectionRequiredQuestion, setSelectedSectionRequiredQuestion] = useState('');

  const openSectionEditModal = (section) => {
    setEditingSection(section);
    setSectionLabelValue(section.label || "");
    setSelectedSectionRequiredQuestion(section?.dependencies?.requiredQuestionIds.length ? section?.dependencies?.requiredQuestionIds[0] : '');
    setSectionEditModalOpen(true);
  };

  const saveSectionDataChange = async () => {
    try {
      await axiosInstance.patch(`/knovia/myInfo/${editingSection._id}/updateUserInfoSectionData`, {
        label: sectionLabelValue,
        requiredQuestionId: selectedSectionRequiredQuestion
      });
      setInfoQuestionSetEntries(prev =>
        prev.map(q => q._id === editingSection._id
          ? { ...q, label: sectionLabelValue, 
              dependencies: { ...q.dependencies, requiredQuestionIds: selectedSectionRequiredQuestion ? [selectedSectionRequiredQuestion] : [] } 
            }
          : q
        )
      );
    } catch (err) {
      console.error("Failed to update section:", err);
    } finally {
      setSectionEditModalOpen(false);
    }
  };

  const [textEditModalOpen, setTextEditModalOpen] = useState(false);
  const [textContentValue, setTextContentValue] = useState("");
  const [editingText, setEditingText] = useState(null);

  const [selectedTextRequiredQuestion, setSelectedTextRequiredQuestion] = useState('');

  const textareaRef = useRef();
  const autoResizeTextarea = (el) => {
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };
  useEffect(() => {
    if (textareaRef.current) {
      autoResizeTextarea(textareaRef.current);
    }
  }, [textContentValue]);
  useEffect(() => {
    if (textEditModalOpen) {
      setTimeout(() => {
        if (textareaRef.current) {
          autoResizeTextarea(textareaRef.current);
        }
      }, 0);
    }
  }, [textEditModalOpen]);

  const openTextEditModal = (textContent) => {
    setEditingText(textContent);
    setTextContentValue(textContent.text || "");
    setSelectedTextRequiredQuestion(textContent?.dependencies?.requiredQuestionIds.length ? textContent?.dependencies?.requiredQuestionIds[0] : '');
    setTextEditModalOpen(true);
  };

  const saveTextContentChange = async () => {
    try {
      await axiosInstance.patch(`/knovia/myInfo/${editingText._id}/updateUserInfoText`, {
        text: textContentValue,
        requiredQuestionId: selectedTextRequiredQuestion
      });
      setInfoQuestionSetEntries(prev =>
        prev.map(q => q._id === editingText._id 
          ? { ...q, text: textContentValue,
              dependencies: { ...q.dependencies, requiredQuestionIds: selectedTextRequiredQuestion ? [selectedTextRequiredQuestion] : [] } 
            } 
          : q
        )
      );
    } catch (err) {
      console.error("Failed to update text content:", err);
    } finally {
      setTextEditModalOpen(false);
    }
  };

  //--------------------------------------------------
  // Image Editing
  //-------------------------------------------------- 
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [uploadImageUrl, setUploadImageUrl] = useState("");
  const [editingImageEntry, setEditingImageEntry] = useState(null);

  const [selectedImageRequiredQuestion, setSelectedImageRequiredQuestion] = useState('');

  const openImageUploadModal = (entry) => {
    setEditingImageEntry(entry);
    setUploadImageUrl(entry.imageUrl || "");
    setSelectedImageRequiredQuestion(entry?.dependencies?.requiredQuestionIds?.length ? entry?.dependencies?.requiredQuestionIds[0] : '');
    setImageModalOpen(true);
  };

  const createdObjectUrls = useRef(new Set());
  const handleUploadImageFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const tempUrl = URL.createObjectURL(file);
    createdObjectUrls.current.add(tempUrl); // track it
    setUploadImageUrl(tempUrl);

    try {
      const filename = await uploadImage(file); // your upload function
      const { blob } = await fetchImage(filename, "original");

      const newUrl = URL.createObjectURL(blob);
      createdObjectUrls.current.add(newUrl);
      setUploadImageUrl(newUrl);

      await axiosInstance.patch(`/knovia/myInfo/${editingImageEntry._id}/updateUserInfoImage`, {
        image: filename
      });

      setInfoQuestionSetEntries(prev =>
        prev.map(q => q._id === editingImageEntry._id ? { ...q, image: filename } : q)
      );

      // Update imageMap
      setImageMap(prev => {
        const newMap = new Map(prev);
        newMap.set(filename, newUrl);
        return newMap;
      });

    } catch (err) {
      console.error("Upload failed:", err);
      setUploadImageUrl("");
    } finally {
      URL.revokeObjectURL(tempUrl);
    }
  };

  const saveImageContentChange = async () => {
    try {
      await axiosInstance.patch(`/knovia/myInfo/${editingImageEntry._id}/updateUserInfoImageMetaData`, {
        requiredQuestionId: selectedImageRequiredQuestion
      });
      setInfoQuestionSetEntries(prev =>
        prev.map(q => q._id === editingImageEntry._id 
          ? { ...q,
              dependencies: { ...q.dependencies, requiredQuestionIds: selectedImageRequiredQuestion ? [selectedImageRequiredQuestion] : [] } 
            } 
          : q
        )
      );
    } catch (err) {
      console.error("Failed to update image metadata:", err);
    } finally {
      setImageModalOpen(false);
    }
  };


  return (
    <>
      <Row style={{ marginBottom: "0.5rem" }}>
        <Col xs={12} style={{ display: "flex", justifyContent: "space-between", padding: "0" }}>

          <Row style={{ width: "100%", margin: "0" }}>
            <Col xs={12} md={8} className="mb-2 mb-md-0">
              <h3>題組中的問題</h3>
              {isSavingEntriesOrder && <span style={{ color: "#f1b34c" }}>Saving Order...</span>}
              {!isSavingEntriesOrder && !isSavedEntriesOrderError &&
                <span style={{ color: isSavedEntriesOrder ? "#34c38f" : "#aeabab" }}>✔ Saved Order</span>
              }
              {!isSavingEntriesOrder && isSavedEntriesOrderError &&
                <span style={{ color: "red" }}>Error Saving Question Order!</span>
              }
            </Col>
            <Col xs={12} md={4} className="justify-content-center justify-content-md-end" style={{ display:"flex", alignItems:"center" }}>
              <Row className="d-flex flex-column">
                <Col xs={6} md={6} className="d-flex align-items-center mb-1">
                  <Button color="outline-info" onClick={handleAddQuestion} style={{ marginRight: "1rem", minWidth: "120px" }}>
                    + 新增題目
                  </Button>
                  <Button color="outline-warning" onClick={handleAddSection} style={{ marginRight: "1rem", minWidth: "120px" }}>
                    + 新增區段
                  </Button>
                </Col>
                <Col xs={6} md={6} className="d-flex align-items-center">
                  <Button color="outline-success" onClick={()=>{handleAddContent("image")}} style={{ marginRight: "1rem", minWidth: "120px" }}>
                    + 新增圖片
                  </Button>
                  <Button color="outline-success" onClick={()=>{handleAddContent("text")}} style={{ marginRight: "1rem", minWidth: "120px" }}>
                    + 新增文字段落
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>

        </Col>
      </Row>

      <Row>
        <Col xs={12} style={{ padding: "0.5rem 3rem", border: "1px solid #bcbbbb", borderRadius: "5px" }}>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="questionsList">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {infoQuestionSetEntries.map((q, index) => {

                    const hasDependency = q?.dependencies?.requiredQuestionIds?.length > 0;
                    const requiredId = q?.dependencies?.requiredQuestionIds?.[0];
                    const prevQuestion = infoQuestionSetEntries.find(e => e._id === requiredId);

                    const hasRefQuestion = q?.dependencies?.refQuestionIds?.length > 0;
                    const refQuestionId = q?.dependencies?.refQuestionIds?.[0];
                    const refQuestion = infoQuestionSetEntries.find(e => e._id === refQuestionId);

                    const hasCompletionMark = q?.completionMarker?.hasRole;
                    const completionMarkerName = q?.completionMarker?.displayName || "";
                    const hasCheckPointMark = q?.checkpointMarker?.hasRole;
                    const checkPointMarkerName = q?.checkpointMarker?.displayName || "";

                    return (

                      <Draggable
                        key={q._id}
                        draggableId={q._id}
                        index={index}
                      >
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            // Optional: let entire row be the "handle" or pick a sub-element
                            {...provided.dragHandleProps}
                          >
                            {/* Single row content */}
                            <Row key={q._id} style={{ minHeight: "5.5rem", marginBottom: "2px" }}
                              className="mb-4 mb-xl-2"
                            >
                              <Col xs={12} md={8} style={{ padding: "0.25rem 0rem" }}
                                className="d-flex flex-column justify-content-center mb-2 mb-xl-0"
                              >
                                
                                <Row className={!hasRefQuestion ? "mb-1" : "mb-1"}>
                                  
                                  <Col xs={6}>
                                    {hasDependency && prevQuestion && (
                                      <p className="mb-0" style={{ fontSize: '11px', color: "#639ed5" }}>
                                        {`${prevQuestion.order}. ${
                                          prevQuestion.label.length > 20
                                            ? prevQuestion.label.slice(0, 20) + '...'
                                            : prevQuestion.label
                                        }`}{" "}
                                        <CornerRightDown size={13} color="#639ed5" strokeWidth={1.8} />
                                      </p>
                                    )}
                                  </Col>
                                  
                                  <Col xs={6} className="d-flex flex-column justify-content-start align-items-end" style={{ paddingRight: "15px" }}>
                                    {hasCheckPointMark && (
                                      <div className="d-flex align-items-center">
                                        <CircleCheckBig size={13} color="#5abd99" strokeWidth={1.8} style={{ margin: "1px 2px 0px 0px" }} />
                                        <p className="mb-0" style={{ fontSize: '11px', color: "#5abd99" }}>
                                          {`${checkPointMarkerName.length > 20
                                              ? checkPointMarkerName.slice(0, 20) + '...'
                                              : checkPointMarkerName
                                          }`}{" "}
                                        </p>
                                      </div>
                                    )}
                                    {hasCompletionMark && (
                                      <div className="d-flex align-items-center">
                                        <Goal size={13} color="#5abd99" strokeWidth={1.8} style={{ margin: "1px 2px 0px 0px" }} />
                                        <p className="mb-0" style={{ fontSize: '11px', color: "#5abd99" }}>
                                          {`${completionMarkerName.length > 20
                                              ? completionMarkerName.slice(0, 20) + '...'
                                              : completionMarkerName
                                          }`}{" "}
                                        </p>
                                      </div>
                                    )}
                                  </Col>
                                </Row>

                                {/*
                                {hasRefQuestion && refQuestion && (
                                  <Row className="mb-1">
                                    <Col xs={12} className="d-flex align-items-center">
                                      <Plus size={13} color="#4eb357" strokeWidth={2.5} style={{ verticalAlign: 'middle' }}/>
                                      <p className="mb-0" style={{ fontSize: '11px', color: "#4eb357" }}>
                                        {`${refQuestion.order}. ${
                                          refQuestion.label.length > 18
                                            ? refQuestion.label.slice(0, 18) + '...'
                                            : refQuestion.label
                                        }`}{" "}
                                      </p>
                                    </Col>
                                  </Row>
                                )}
                                */}
                                
                                {q.type==="question" &&
                                  <Row style={{ width: "100%", height: "100%", margin: "0", border: "1px solid #bcbbbb", borderRadius: "5px" }}>
                                    <Col xs={6} xl={3}>
                                      {q?.dependencies?.activationConditionSets.map((set, j) => {
                                        const isLastConditionGroup = j === q.dependencies.activationConditionSets.length-1;
                                        return (
                                          <div key={j} style={{ padding: "0.5rem 0rem 1.2rem 0rem", 
                                            borderBottom: !isLastConditionGroup ? '1px solid #bcbbbb' : "none"
                                          }}>
                                            <Row>
                                              <Col xs={12} xl={4} style={{ display: "flex", justifyContent: "center", paddingTop:"0.5rem" }}>
                                                <strong>群組{j + 1}</strong>
                                              </Col>
                                              <Col xs={12} xl={8} style={{ paddingTop:"1.5rem" }}>
                                                {set.conditions.map((condition, k) => {
                                                  return (
                                                    <div key={k}>
                                                      <p style={{ margin:"0" }}>{`(${k+1}) ${condition}`}</p>
                                                    </div>
                                                  )
                                                })}
                                              </Col>
                                            </Row>
                                          </div>
                                        )
                                      })}
                                    </Col>


                                    <Col xs={6} xl={3}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        backgroundColor: "#d3eaff",
                                        minHeight: "5rem",
                                        padding: "10px 12px 15px 12px"
                                      }}
                                    >
                                      <pre 
                                        style={{ 
                                          margin: "0",
                                          color: "#495057",
                                          fontFamily: '"Noto Sans", sans-serif',
                                          fontSize: "13px",
                                          whiteSpace: "pre-wrap", // allow wrapping
                                          wordBreak: "break-word" 
                                        }}
                                      >
                                        {`${q.order}. ${q.label}`}{" "}
                                      </pre>
                                    </Col>

                                    <Col xs={12} xl={3}
                                      style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        backgroundColor: "#f4f4f4",
                                        minHeight: "1.5rem"
                                      }}
                                    >
                                      {hasRefQuestion && refQuestion && (
                                        <Row className="mt-2 mb-1">
                                          <Col xs={12} className="d-flex align-items-center">
                                            <p style={{ fontSize: '12px', color: "#f2a16e", marginBottom: "1px" }}>
                                              {`+ ${refQuestion.order}. ${
                                                refQuestion.label.length > 200
                                                  ? `${refQuestion.label.slice(0, 200)}...`
                                                  : `${refQuestion.label}`
                                              }`}{" "}
                                            </p>
                                          </Col>
                                        </Row>
                                      )}
                                      {q?.verifyRuleAI?.map((set, j) => {
                                        const isLastSetOfQuestion = j === q.verifyRuleAI.length - 1;
                                        return (
                                          <div key={j} className="pt-1 pt-xl-2 pb-2 pb-xl-3" style={{
                                            margin: "0.3rem 0rem",
                                            borderBottom: !isLastSetOfQuestion ? '1px solid #bcbbbb' : "none"
                                          }}>
                                            <Row>
                                              <Col xs={12} xl={4} style={{ display: "flex", justifyContent: "center" }}
                                                className="pt-0 pt-xl-1"
                                              >
                                                <strong>群組{j + 1}</strong>
                                              </Col>
                                              <Col xs={12} xl={8} /*style={{ paddingTop:"1.5rem" }}*/
                                                className="pt-2 pt-xl-3"
                                              >
                                                {set.conditions.map((condition, k) => (
                                                  <div key={k}>
                                                    <p style={{ margin:"0" }}>{`(${k+1}) ${condition}`}</p>
                                                  </div>
                                                ))}
                                              </Col>
                                            </Row>
                                          </div>
                                        );
                                      })}
                                    </Col>


                                    <Col xs={12} xl={3}
                                      style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        minHeight: "1.5rem"
                                      }}
                                    >
                                      {q?.dependencies?.affectedAttributes.map((attribute, j) => {
                                        const isLastAttribute = j === q.dependencies.affectedAttributes.length-1;
                                        return (
                                          <div key={j} style={{ margin: "0.3rem 0rem", padding: "0.5rem 0rem 1rem 0rem", 
                                            borderBottom: !isLastAttribute ? '1px solid #bcbbbb' : "none"
                                          }}>
                                            <p style={{ margin:"0" }}>{attribute}</p>
                                          </div>
                                        )
                                      })}
                                    </Col>

                                  </Row>
                                }

                                {q.type==="section" &&
                                  <Row style={{ width: "100%", height: "100%", margin: "0", border: "1px solid #bcbbbb", borderRadius: "5px" }}>
                                    <Col xs={4} xl={4}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        backgroundColor: "#fff0d5",
                                        minHeight: "5rem"
                                      }}
                                    >
                                      <p style={{ margin: "0" }}>
                                        {`${q.order}. ${q.label}`}{" "}
                                      </p>
                                    </Col>
                                    <Col xs={8} xl={8}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        backgroundColor: "#fff0d5",
                                        minHeight: "5rem"
                                      }}
                                    >
                                      <div style={{ width: "100%", height: "8px", borderTop:"1px solid #8b8a8a", borderRight:"1px solid #8b8a8a", marginTop: "10px" }}>
                                      </div>
                                    </Col>
                                  </Row>
                                }

                                {q.type==="text" &&
                                  <Row style={{ width: "100%", height: "100%", margin: "0", border: "1px solid #bcbbbb", borderRadius: "5px" }}>
                                    <Col xs={6} xl={6}>
                                      {q?.dependencies?.activationConditionSets.map((set, j) => {
                                        const isLastConditionGroup = j === q.dependencies.activationConditionSets.length-1;
                                        return (
                                          <div key={j} style={{ padding: "0.5rem 0rem 1.2rem 0rem", 
                                            borderBottom: !isLastConditionGroup ? '1px solid #bcbbbb' : "none"
                                          }}>
                                            <Row>
                                              <Col xs={12} xl={4} style={{ display: "flex", justifyContent: "center", paddingTop:"0.5rem" }}>
                                                <strong>群組{j + 1}</strong>
                                              </Col>
                                              <Col xs={12} xl={8} style={{ paddingTop:"1.5rem" }}>
                                                {set.conditions.map((condition, k) => {
                                                  return (
                                                    <div key={k}>
                                                      <p style={{ margin:"0" }}>{`(${k+1}) ${condition}`}</p>
                                                    </div>
                                                  )
                                                })}
                                              </Col>
                                            </Row>
                                          </div>
                                        )
                                      })}
                                    </Col>
                                    <Col xs={6} xl={6}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        backgroundColor: "#d3eaff",
                                        minHeight: "5rem"
                                      }}
                                    >
                                      <p style={{ margin: "0" }}>
                                        {`${q.order}. [文字段落]`}{" "}
                                      </p>
                                    </Col>
                                    <Col xs={12} xl={12}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: "#f4f4f4",
                                        minHeight: "5rem",
                                        padding: "12px 30px"
                                      }}
                                    >
                                      <pre 
                                        style={{ 
                                          margin: "0",
                                          color: "#495057",
                                          fontFamily: '"Noto Sans", sans-serif',
                                          fontSize: "13px",
                                          whiteSpace: "pre-wrap", // allow wrapping
                                          wordBreak: "break-word" 
                                        }}
                                      >{q.text}
                                      </pre>
                                    </Col>
                                  </Row>
                                }

                                {q.type==="image" &&
                                  <Row style={{ width: "100%", height: "100%", margin: "0", border: "1px solid #bcbbbb", borderRadius: "5px" }}>
                                    <Col xs={6} xl={6}>
                                      {q?.dependencies?.activationConditionSets.map((set, j) => {
                                        const isLastConditionGroup = j === q.dependencies.activationConditionSets.length-1;
                                        return (
                                          <div key={j} style={{ padding: "0.5rem 0rem 1.2rem 0rem", 
                                            borderBottom: !isLastConditionGroup ? '1px solid #bcbbbb' : "none"
                                          }}>
                                            <Row>
                                              <Col xs={12} xl={4} style={{ display: "flex", justifyContent: "center", paddingTop:"0.5rem" }}>
                                                <strong>群組{j + 1}</strong>
                                              </Col>
                                              <Col xs={12} xl={8} style={{ paddingTop:"1.5rem" }}>
                                                {set.conditions.map((condition, k) => {
                                                  return (
                                                    <div key={k}>
                                                      <p style={{ margin:"0" }}>{`(${k+1}) ${condition}`}</p>
                                                    </div>
                                                  )
                                                })}
                                              </Col>
                                            </Row>
                                          </div>
                                        )
                                      })}
                                    </Col>
                                    <Col xs={6} xl={6}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        backgroundColor: "#d3eaff",
                                        minHeight: "5rem"
                                      }}
                                    >
                                      <p style={{ margin: "0" }}>
                                        {`${q.order}. [圖片內容]`}{" "}
                                      </p>
                                    </Col>
                                    <Col xs={12} xl={12} className="d-flex flex-column align-items-center justify-content-center"
                                      style={{ backgroundColor: "#f4f4f4", minHeight: "10rem" }}
                                    >
                                      {/*
                                        <p>{q.image}</p>
                                        <p>{imageMap.get(q.image)}</p>
                                      */}
                                      <div style={{ margin: "14px auto"}}>
                                        <img src={imageMap.get(q.image) || placeholderSmall} alt="preview" 
                                          style={{ height: '8rem', borderRadius: '5px', objectFit: "cover" }} 
                                        />
                                      </div>
                                    </Col>
                                  </Row>
                                }

                              </Col>

                              <Col xs={12} md={4} className="d-flex justify-content-center align-items-center">
                                <Row style={{ width: "100%" }}>
                                  <Col xs={6} md={12} lg={6} className="d-flex justify-content-center align-items-center mb-2 md-lg-0">
                                    <Button color="outline-info" style={{ padding: "7px 8px", marginRight: "0.3rem" }}
                                      onClick={(e) => {
                                        if (q.type === 'question') {
                                          navigate(`/myInfo/editQuestion/${q._id}`);
                                        } else if (q.type === 'section') {
                                          openSectionEditModal(q);
                                        } else if (q.type === 'text') {
                                          openTextEditModal(q);
                                        } else if (q.type === 'image') {
                                          openImageUploadModal(q);
                                        }
                                      }}
                                    >
                                      <i className="bx bxs-pencil" />
                                    </Button>

                                    <Button color="outline-danger" 
                                      onClick={(e) => {
                                        if (q.type === 'question') {
                                          handleDeleteQuestion(q._id);
                                        } else if (q.type === 'section') {
                                          handleDeleteSection(q._id);
                                        } else if (q.type === 'text' || q.type === 'image') {
                                          handleDeleteContent(q._id, q.type);
                                        }
                                      }}
                                    >
                                      -
                                    </Button>
                                  </Col>

                                  {/* Question Edit Status */}
                                  <Col xs={6} md={12} lg={6} className="ps-0 pe-0 ps-md-4 pe-md-3 ps-lg-0 pe-lg-0"
                                    style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}  
                                  >
                                    <div className="w-100 px-1 px-md-0">
                                      <StatusSelect
                                        value={q.status}
                                        onChange={(newStatus) => handleStatusChange(q, newStatus)}
                                        options={statusOptions}
                                        selectId={q._id}
                                        openSelectId={openSelectId}
                                        setOpenSelectId={setOpenSelectId}
                                      />
                                    </div>

                                    <div className="w-100 px-1 px-md-0">
                                      {isSavingStatus[q._id] && <span style={{ color: "#f1b34c" }}>Saving...</span>}
                                      {!isSavingStatus[q._id] && (
                                        <span style={{ color: isSavedStatus[q._id] ? "#34c38f" : "#aeabab" }}>
                                          ✔ Saved!
                                        </span>
                                      )}
                                    </div>

                                  </Col>
                                </Row>
                              </Col>



                            </Row>
                          </div>
                        )}
                      </Draggable>
                    )
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

        </Col>
      </Row>

      <Modal isOpen={sectionEditModalOpen} toggle={() => setSectionEditModalOpen(false)}>
        <ModalHeader toggle={() => setSectionEditModalOpen(false)}>Edit Section</ModalHeader>
        <ModalBody>
          <Input
            value={sectionLabelValue}
            onChange={(e) => {setSectionLabelValue(e.target.value);}}
          />

          <Select
            className="mt-3"
            value={[
              { value: '', label: '— No required question —' },
              ...infoQuestionSetEntries
                .map(q => ({
                  value: q._id,
                  label: `${q.order}. ${q.label}`
                }))
            ].find(option => option.value === selectedSectionRequiredQuestion)}
            onChange={(selectedOption) => setSelectedSectionRequiredQuestion(selectedOption.value)}
            options={[
              { value: '', label: '— No required question —' },
              ...infoQuestionSetEntries
                .filter(q => q.type === "question")
                .filter(q => q.order < editingSection?.order)
                .map(q => ({
                  value: q._id,
                  label: `${q.order}. ${q.label}`
                }))
            ]}
            styles={{
              menu: (provided) => ({
                ...provided,
                backgroundColor: 'white',
                zIndex: 9999, // ensure it's above modal
              }),
              option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isFocused ? '#f0f0f0' : 'white',
                color: '#495057',
              }),
              control: (provided) => ({
                ...provided,
                backgroundColor: 'white',
                color: 'black',
              }),
            }}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={saveSectionDataChange}>Save</Button>
          <Button color="secondary" onClick={() => setSectionEditModalOpen(false)}>Cancel</Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={textEditModalOpen} toggle={() => setTextEditModalOpen(false)}>
        <ModalHeader toggle={() => setTextEditModalOpen(false)}>Edit Text Content</ModalHeader>
        <ModalBody>
          <Input
            type="textarea"
            value={textContentValue}
            onChange={(e) => {
              const value = e.target.value.slice(0, 500); // enforce max 500 chars
              setTextContentValue(value);
              autoResizeTextarea(e.target);
            }}
            onPaste={(e) => {
              const pasted = e.clipboardData.getData("text");
              if (textContentValue.length + pasted.length > 500) {
                e.preventDefault();
                const remaining = 500 - textContentValue.length;
                setTextContentValue((prev) => prev + pasted.slice(0, remaining));
              }
            }}
            rows={1}
            innerRef={textareaRef}
            style={{
              resize: "none", // prevent manual resizing
              overflow: "hidden", // prevent scrollbars
              transition: "height 0.2s ease"
            }}
            maxLength={500}
          />
          <small className="text-muted">{textContentValue.length}/500</small>

          <Select
            className="mt-3"
            value={[
              { value: '', label: '— No required question —' },
              ...infoQuestionSetEntries
                .map(q => ({
                  value: q._id,
                  label: `${q.order}. ${q.label}`
                }))
            ].find(option => option.value === selectedTextRequiredQuestion)}
            onChange={(selectedOption) => setSelectedTextRequiredQuestion(selectedOption.value)}
            options={[
              { value: '', label: '— No required question —' },
              ...infoQuestionSetEntries
                .filter(q => q.type === "question")
                .filter(q => q.order < editingText?.order)
                .map(q => ({
                  value: q._id,
                  label: `${q.order}. ${q.label}`
                }))
            ]}
            styles={{
              menu: (provided) => ({
                ...provided,
                backgroundColor: 'white',
                zIndex: 9999, // ensure it's above modal
              }),
              option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isFocused ? '#f0f0f0' : 'white',
                color: '#495057',
              }),
              control: (provided) => ({
                ...provided,
                backgroundColor: 'white',
                color: 'black',
              }),
            }}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={saveTextContentChange}>Save</Button>
          <Button color="secondary" onClick={() => setTextEditModalOpen(false)}>Cancel</Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={imageModalOpen} toggle={() => setImageModalOpen(false)}>
        <ModalHeader toggle={() => setImageModalOpen(false)}>Upload Image</ModalHeader>
        <ModalBody>
          <p style={{ fontSize: "16px" }}>顯示圖片內容</p>
          <Input type="file" accept="image/*" onChange={handleUploadImageFileChange} />
          <div style={{ marginTop: "10px" }} className="d-flex justify-content-center">
            <img src={uploadImageUrl || imageMap.get(editingImageEntry?.image) || placeholderSmall} alt="preview" 
              style={{ width: '80%', borderRadius: '10px' }} 
            />
          </div>

          <Select
            className="mt-3"
            value={[
              { value: '', label: '— No required question —' },
              ...infoQuestionSetEntries
                .map(q => ({
                  value: q._id,
                  label: `${q.order}. ${q.label}`
                }))
            ].find(option => option.value === selectedImageRequiredQuestion)}
            onChange={(selectedOption) => setSelectedImageRequiredQuestion(selectedOption.value)}
            options={[
              { value: '', label: '— No required question —' },
              ...infoQuestionSetEntries
                .filter(q => q.type === "question")
                .filter(q => q.order < editingImageEntry?.order)
                .map(q => ({
                  value: q._id,
                  label: `${q.order}. ${q.label}`
                }))
            ]}
            styles={{
              menu: (provided) => ({
                ...provided,
                backgroundColor: 'white',
                zIndex: 9999, // ensure it's above modal
              }),
              option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isFocused ? '#f0f0f0' : 'white',
                color: '#495057',
              }),
              control: (provided) => ({
                ...provided,
                backgroundColor: 'white',
                color: 'black',
              }),
            }}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={saveImageContentChange}>Save</Button>
          <Button color="secondary" onClick={() => setImageModalOpen(false)}>Close</Button>
        </ModalFooter>
      </Modal>
    </>

  );
};

export default withTranslation()(QuestionSetEntriesTab);


